import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const AutoDetailingPackages = () => {
  useEffect(() => {
    analytics.track('Template Page View', { template: 'Auto Detailing Quote Template' });
  }, []);

  return (
    <SEOPageLayout
      title="Free Auto Detailing Quote Template - Tiered Options (Good/Better/Best) | QuoteTier"
      description="Free auto detailing quote template with Good/Better/Best tiers. Let customers upgrade from a basic wash to full ceramic themselves — and watch your average ticket climb."
      canonical="/templates/auto-detailing-packages-template"
      ogTitle="Free Auto Detailing Quote Template"
      ogDescription="Detailers using tiered quotes routinely upsell basic wash bookings into ceramic coating jobs. Ready-to-customize template, free forever."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Free <span className="text-primary font-bold">Auto Detailing Quote Template</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Stop quoting flat rates. Show a basic wash, an interior detail, and a full ceramic package on the same quote — and let customers upgrade themselves.
            </p>
            <div className="max-w-2xl mx-auto mb-10 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
              <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
                Most basic-wash customers don't know ceramic coating exists at quote time — that's why they never ask for it. A three-tier quote makes the upgrade path obvious, which is why detailers running tiered pricing routinely see 20–40% of basic bookings roll over into paint protection or interior restoration.
              </p>
            </div>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center mb-3">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Use This Template Free
                </Button>
              </Link>
            </div>
            <p className="font-jetbrains text-sm text-gray-500 mb-6">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Sample Tiers</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Basic Wash</CardTitle><CardDescription>The Budget Option</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Exterior hand wash & dry</li>
                      <li>Tire cleaning & dressing</li>
                      <li>Interior vacuum</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black bg-black text-white">
              <CardHeader><CardTitle className="font-instrument text-xl text-white">Interior Detail</CardTitle><CardDescription className="text-gray-300">Best Seller</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-300 list-disc list-inside">
                      <li>Everything in Basic Wash</li>
                      <li>Deep interior clean & shampoo</li>
                      <li>Leather conditioning</li>
                      <li>Odour elimination</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Full Ceramic</CardTitle><CardDescription>Ultimate Protection</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Everything in Interior Detail</li>
                      <li>Paint decontamination & clay</li>
                      <li>2-year ceramic coating</li>
                      <li>Glass & wheel coating</li>
                  </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Using This Template Now</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Get Free Access
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default AutoDetailingPackages;
